<script setup lang="ts">
import { computed, ref } from "vue";
import {
  Check,
  ChevronDown,
  Mail,
  Phone,
  Plus,
  Trash2,
  User,
} from "@lucide/vue";
import type {
  FormBlock,
  FormButtonAction,
  FormButtonLayout,
  FormFieldData,
  FormFieldType,
  FormSyncIntegrationId,
} from "@aswin.dev/types";
import { createFormField } from "@aswin.dev/types";
import {
  addItemBtnClass,
  inputClass,
  labelClass,
} from "../../constants/styleConstants";
import { useI18n } from "../../composables/useI18n";
import ColorPicker from "../ColorPicker.vue";

const props = defineProps<{
  block: FormBlock;
}>();

const emit = defineEmits<{
  (e: "update", updates: Partial<FormBlock>): void;
}>();

const { t } = useI18n();

type Tab = "sync" | "fields" | "button" | "terms";
const activeTab = ref<Tab>("fields");

const tabs: { id: Tab; label: string }[] = [
  { id: "sync", label: t.form.tabs.sync },
  { id: "fields", label: t.form.tabs.fields },
  { id: "button", label: t.form.tabs.button },
  { id: "terms", label: t.form.tabs.terms },
];

function tabClass(tab: Tab): string {
  const base =
    "tpl:flex-1 tpl:cursor-pointer tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:px-3 tpl:py-2 tpl:text-xs tpl:font-medium tpl:transition-all tpl:duration-[120ms]";
  if (activeTab.value === tab) {
    return `${base} tpl:text-[var(--tpl-text)] tpl:bg-[var(--tpl-bg)] tpl:shadow-[var(--tpl-shadow-md)]`;
  }
  return `${base} tpl:text-[var(--tpl-text-muted)] tpl:bg-transparent hover:tpl:text-[var(--tpl-text)]`;
}

function updateField<K extends keyof FormBlock>(
  key: K,
  value: FormBlock[K],
): void {
  emit("update", { [key]: value } as Partial<FormBlock>);
}

// ---------------------------------------------------------------------------
// Sync tab — list of marketing platforms (curated, static for now).
// ---------------------------------------------------------------------------

interface IntegrationItem {
  id: FormSyncIntegrationId;
  label: string;
  initial: string;
  color: string;
}

const integrations: IntegrationItem[] = [
  { id: "acoustic", label: "Acoustic", initial: "A", color: "#5b21b6" },
  { id: "actito", label: "Actito", initial: "a", color: "#16a34a" },
  {
    id: "active_campaign",
    label: "Active Campaign",
    initial: ">",
    color: "#1e40af",
  },
  { id: "attentive", label: "Attentive", initial: "A", color: "#facc15" },
  { id: "braze", label: "Braze", initial: "b", color: "#111827" },
  { id: "brevo", label: "Brevo", initial: "B", color: "#10b981" },
  {
    id: "campaign_monitor",
    label: "Campaign Monitor",
    initial: "M",
    color: "#0f172a",
  },
  {
    id: "constant_contact",
    label: "Constant Contact",
    initial: "≈",
    color: "#ea580c",
  },
  { id: "customer_io", label: "Customer.io", initial: "•", color: "#a855f7" },
  { id: "dotdigital", label: "Dotdigital", initial: "◎", color: "#dc2626" },
  { id: "drip", label: "Drip", initial: "⋯", color: "#0f172a" },
  { id: "emarsys", label: "Emarsys", initial: "≣", color: "#16a34a" },
  { id: "klaviyo", label: "Klaviyo", initial: "K", color: "#1e1b4b" },
  { id: "mailchimp", label: "Mailchimp", initial: "M", color: "#38bdf8" },
  { id: "hubspot", label: "HubSpot", initial: "H", color: "#f97316" },
];

function selectIntegration(id: FormSyncIntegrationId): void {
  if (props.block.syncIntegrationId === id) {
    updateField("syncIntegrationId", null);
  } else {
    updateField("syncIntegrationId", id);
  }
}

// ---------------------------------------------------------------------------
// Fields tab — list editor + add menu.
// ---------------------------------------------------------------------------

const FIELD_TYPE_META: Record<
  FormFieldType,
  { label: string; icon: typeof Mail }
> = {
  firstName: { label: t.form.fieldTypes.firstName, icon: User },
  lastName: { label: t.form.fieldTypes.lastName, icon: User },
  email: { label: t.form.fieldTypes.email, icon: Mail },
  phone: { label: t.form.fieldTypes.phone, icon: Phone },
};

const ALL_FIELD_TYPES: FormFieldType[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
];

const expandedFieldId = ref<string | null>(null);
const showAddFieldMenu = ref(false);

const availableFieldTypes = computed(() => {
  // First name, last name, email and phone can each be added once for now.
  const used = new Set(props.block.fields.map((f) => f.type));
  return ALL_FIELD_TYPES.filter((type) => !used.has(type));
});

function toggleField(id: string): void {
  expandedFieldId.value = expandedFieldId.value === id ? null : id;
}

function addField(type: FormFieldType): void {
  const newField = createFormField(type);
  emit("update", { fields: [...props.block.fields, newField] });
  expandedFieldId.value = newField.id;
  showAddFieldMenu.value = false;
}

function deleteField(id: string): void {
  emit("update", {
    fields: props.block.fields.filter((f) => f.id !== id),
  });
}

function patchField(id: string, patch: Partial<FormFieldData>): void {
  emit("update", {
    fields: props.block.fields.map((f) =>
      f.id === id ? ({ ...f, ...patch } as FormFieldData) : f,
    ),
  });
}

function fieldIcon(type: FormFieldType) {
  return FIELD_TYPE_META[type].icon;
}

// ---------------------------------------------------------------------------
// Button tab.
// ---------------------------------------------------------------------------

const buttonActionOptions: { value: FormButtonAction; label: string }[] = [
  { value: "next_step", label: t.form.buttonActions.nextStep },
  { value: "close_popup", label: t.form.buttonActions.closePopup },
  { value: "load_page", label: t.form.buttonActions.loadPage },
  { value: "none", label: t.form.buttonActions.none },
];

function setButtonLayout(layout: FormButtonLayout): void {
  updateField("buttonLayout", layout);
}
</script>

<template>
  <div>
    <!-- Tabs strip -->
    <div
      role="tablist"
      class="tpl:mb-4 tpl:flex tpl:gap-1 tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-active)] tpl:p-1"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :class="tabClass(tab.id)"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Sync tab -->
    <div v-if="activeTab === 'sync'">
      <p
        class="tpl:mb-3 tpl:text-[10px] tpl:font-semibold tpl:tracking-wider tpl:uppercase tpl:text-[var(--tpl-text-muted)]"
      >
        {{ t.form.sync.heading }}
      </p>
      <div class="tpl:grid tpl:grid-cols-3 tpl:gap-2">
        <button
          v-for="item in integrations"
          :key="item.id"
          type="button"
          class="tpl:relative tpl:flex tpl:cursor-pointer tpl:flex-col tpl:items-center tpl:justify-center tpl:gap-1.5 tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:border-transparent tpl:bg-[var(--tpl-bg-active)] tpl:px-2 tpl:py-3 tpl:text-xs tpl:font-medium tpl:text-[var(--tpl-text)] tpl:transition-all tpl:duration-150 hover:tpl:border-[var(--tpl-border)] hover:tpl:bg-[var(--tpl-bg-hover)]"
          :class="
            block.syncIntegrationId === item.id
              ? 'tpl:!border-[var(--tpl-primary)] tpl:!bg-[var(--tpl-primary-light)]'
              : ''
          "
          @click="selectIntegration(item.id)"
        >
          <span
            class="tpl:flex tpl:size-9 tpl:items-center tpl:justify-center tpl:rounded-md tpl:text-base tpl:font-bold tpl:text-white"
            :style="{ backgroundColor: item.color }"
          >
            {{ item.initial }}
          </span>
          <span class="tpl:text-center tpl:leading-tight">{{
            item.label
          }}</span>
          <Check
            v-if="block.syncIntegrationId === item.id"
            class="tpl:absolute tpl:right-1 tpl:top-1 tpl:text-[var(--tpl-primary)]"
            :size="14"
            :stroke-width="2.5"
          />
        </button>
      </div>
    </div>

    <!-- Fields tab -->
    <div v-else-if="activeTab === 'fields'">
      <div class="tpl:flex tpl:flex-col tpl:gap-2">
        <div
          v-for="field in block.fields"
          :key="field.id"
          class="tpl:overflow-hidden tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-active)]"
        >
          <button
            type="button"
            class="tpl:flex tpl:w-full tpl:cursor-pointer tpl:items-center tpl:justify-between tpl:gap-2 tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-2.5 tpl:text-left"
            @click="toggleField(field.id)"
          >
            <span
              class="tpl:flex tpl:items-center tpl:gap-2 tpl:text-sm tpl:font-medium tpl:text-[var(--tpl-text)]"
            >
              <component
                :is="fieldIcon(field.type)"
                :size="14"
                :stroke-width="1.75"
              />
              {{ FIELD_TYPE_META[field.type].label }}
            </span>
            <span class="tpl:flex tpl:items-center tpl:gap-1">
              <button
                type="button"
                class="tpl:flex tpl:size-7 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-danger)]"
                :title="t.form.fields.remove"
                @click.stop="deleteField(field.id)"
              >
                <Trash2 :size="14" :stroke-width="1.75" />
              </button>
              <ChevronDown
                class="tpl:transition-transform tpl:duration-200"
                :class="
                  expandedFieldId === field.id
                    ? 'tpl:rotate-0'
                    : 'tpl:-rotate-90'
                "
                :size="14"
                :stroke-width="2"
              />
            </span>
          </button>

          <div
            v-if="expandedFieldId === field.id"
            class="tpl:border-t tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:pt-3 tpl:pb-3"
          >
            <div class="tpl:mb-3">
              <label :class="labelClass">
                {{ t.form.fields.label }}
                <span
                  class="tpl:ml-1 tpl:text-[var(--tpl-text-dim)] tpl:font-normal"
                  >({{ t.form.fields.optional }})</span
                >
              </label>
              <input
                type="text"
                :class="inputClass"
                :value="field.label"
                :placeholder="t.form.fields.labelPlaceholder"
                @input="
                  patchField(field.id, {
                    label: ($event.target as HTMLInputElement).value,
                  })
                "
              />
            </div>

            <div class="tpl:mb-3">
              <label :class="labelClass">{{ t.form.fields.placeholder }}</label>
              <input
                type="text"
                :class="inputClass"
                :value="field.placeholder"
                @input="
                  patchField(field.id, {
                    placeholder: ($event.target as HTMLInputElement).value,
                  })
                "
              />
            </div>

            <div class="tpl:mb-3">
              <label :class="labelClass">{{ t.form.fields.name }}</label>
              <input
                type="text"
                :class="inputClass"
                :value="field.name"
                spellcheck="false"
                autocomplete="off"
                @input="
                  patchField(field.id, {
                    name: ($event.target as HTMLInputElement).value.trim(),
                  })
                "
              />
            </div>

            <!-- Email-only: block existing emails -->
            <template v-if="field.type === 'email'">
              <label
                class="tpl:mb-2 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
              >
                <input
                  type="checkbox"
                  class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
                  :checked="field.blockExisting ?? false"
                  @change="
                    patchField(field.id, {
                      blockExisting: ($event.target as HTMLInputElement)
                        .checked,
                    })
                  "
                />
                {{ t.form.fields.blockExisting }}
              </label>
              <p
                class="tpl:mb-3 tpl:text-xs tpl:text-[var(--tpl-text-muted)] tpl:leading-snug"
              >
                {{ t.form.fields.blockExistingHint }}
              </p>

              <template v-if="field.blockExisting">
                <label
                  class="tpl:mb-1.5 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="radio"
                    class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
                    :checked="
                      (field.blockExistingScope ?? 'all_campaigns') ===
                      'all_campaigns'
                    "
                    @change="
                      patchField(field.id, {
                        blockExistingScope: 'all_campaigns',
                      })
                    "
                  />
                  {{ t.form.fields.blockExistingAll }}
                </label>
                <label
                  class="tpl:mb-3 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
                >
                  <input
                    type="radio"
                    class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
                    :checked="field.blockExistingScope === 'this_campaign'"
                    @change="
                      patchField(field.id, {
                        blockExistingScope: 'this_campaign',
                      })
                    "
                  />
                  {{ t.form.fields.blockExistingThis }}
                </label>

                <div class="tpl:mb-3">
                  <label :class="labelClass">{{
                    t.form.fields.errorMessage
                  }}</label>
                  <input
                    type="text"
                    :class="inputClass"
                    :value="field.blockExistingMessage ?? ''"
                    @input="
                      patchField(field.id, {
                        blockExistingMessage: (
                          $event.target as HTMLInputElement
                        ).value,
                      })
                    "
                  />
                </div>
              </template>
            </template>

            <label
              class="tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
              :class="
                field.type === 'email' && field.blockExisting
                  ? 'tpl:opacity-60'
                  : ''
              "
            >
              <input
                type="checkbox"
                class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
                :checked="field.required"
                :disabled="field.type === 'email' && field.blockExisting"
                @change="
                  patchField(field.id, {
                    required: ($event.target as HTMLInputElement).checked,
                  })
                "
              />
              {{ t.form.fields.required }}
            </label>
          </div>
        </div>
      </div>

      <!-- Add field -->
      <div class="tpl:relative tpl:mt-3">
        <button
          v-if="!showAddFieldMenu"
          type="button"
          :class="addItemBtnClass"
          :disabled="availableFieldTypes.length === 0"
          @click="showAddFieldMenu = true"
        >
          <Plus :size="14" :stroke-width="2" />
          {{ t.form.fields.addField }}
        </button>

        <div
          v-else
          class="tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:p-1 tpl:shadow-[var(--tpl-shadow-md)]"
        >
          <button
            v-for="type in availableFieldTypes"
            :key="type"
            type="button"
            class="tpl:flex tpl:w-full tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:bg-transparent tpl:px-2.5 tpl:py-2 tpl:text-left tpl:text-sm tpl:text-[var(--tpl-text)] hover:tpl:bg-[var(--tpl-bg-hover)]"
            @click="addField(type)"
          >
            <component
              :is="FIELD_TYPE_META[type].icon"
              :size="14"
              :stroke-width="1.75"
            />
            {{ FIELD_TYPE_META[type].label }}
          </button>
          <button
            v-if="availableFieldTypes.length === 0"
            type="button"
            class="tpl:px-2.5 tpl:py-2 tpl:text-xs tpl:text-[var(--tpl-text-muted)]"
            disabled
          >
            {{ t.form.fields.noMoreFields }}
          </button>
          <button
            type="button"
            class="tpl:mt-1 tpl:flex tpl:w-full tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:bg-transparent tpl:px-2.5 tpl:py-1.5 tpl:text-xs tpl:text-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]"
            @click="showAddFieldMenu = false"
          >
            {{ t.form.fields.cancel }}
          </button>
        </div>
      </div>
    </div>

    <!-- Button tab -->
    <div v-else-if="activeTab === 'button'">
      <div class="tpl:mb-4">
        <label :class="labelClass">{{ t.form.button.submitText }}</label>
        <input
          type="text"
          :class="inputClass"
          :value="block.submitButtonText"
          @input="
            updateField(
              'submitButtonText',
              ($event.target as HTMLInputElement).value,
            )
          "
        />
      </div>

      <div class="tpl:mb-4">
        <label :class="labelClass">{{ t.form.button.actionOnClick }}</label>
        <select
          :class="inputClass"
          :value="block.buttonAction"
          @change="
            updateField(
              'buttonAction',
              ($event.target as HTMLSelectElement).value as FormButtonAction,
            )
          "
        >
          <option
            v-for="opt in buttonActionOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>

      <div v-if="block.buttonAction === 'load_page'" class="tpl:mb-4">
        <label :class="labelClass">{{ t.form.button.url }}</label>
        <input
          type="text"
          :class="inputClass"
          placeholder="https://..."
          :value="block.buttonUrl ?? ''"
          @input="
            updateField('buttonUrl', ($event.target as HTMLInputElement).value)
          "
        />
      </div>

      <div class="tpl:mb-4">
        <label
          class="tpl:mb-2 tpl:block tpl:text-[10px] tpl:font-semibold tpl:tracking-wider tpl:uppercase tpl:text-[var(--tpl-text-muted)]"
        >
          {{ t.form.button.position }}
        </label>
        <div class="tpl:grid tpl:grid-cols-2 tpl:gap-2">
          <button
            type="button"
            class="tpl:flex tpl:cursor-pointer tpl:flex-col tpl:items-center tpl:gap-2 tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:px-3 tpl:py-3 tpl:text-xs tpl:font-medium tpl:transition-all tpl:duration-150"
            :class="
              block.buttonLayout === 'one_line'
                ? 'tpl:border-[var(--tpl-primary)] tpl:bg-[var(--tpl-primary-light)] tpl:text-[var(--tpl-text)]'
                : 'tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text-muted)] hover:tpl:border-[var(--tpl-text-muted)]'
            "
            @click="setButtonLayout('one_line')"
          >
            <svg
              width="56"
              height="20"
              viewBox="0 0 56 20"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="2"
                y="6"
                width="34"
                height="8"
                rx="2"
                fill="currentColor"
                opacity="0.4"
              />
              <rect
                x="38"
                y="4"
                width="16"
                height="12"
                rx="2"
                fill="currentColor"
                opacity="0.9"
              />
            </svg>
            {{ t.form.button.oneLine }}
          </button>
          <button
            type="button"
            class="tpl:flex tpl:cursor-pointer tpl:flex-col tpl:items-center tpl:gap-2 tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:px-3 tpl:py-3 tpl:text-xs tpl:font-medium tpl:transition-all tpl:duration-150"
            :class="
              block.buttonLayout === 'two_lines'
                ? 'tpl:border-[var(--tpl-primary)] tpl:bg-[var(--tpl-primary-light)] tpl:text-[var(--tpl-text)]'
                : 'tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:text-[var(--tpl-text-muted)] hover:tpl:border-[var(--tpl-text-muted)]'
            "
            @click="setButtonLayout('two_lines')"
          >
            <svg
              width="56"
              height="28"
              viewBox="0 0 56 28"
              fill="none"
              aria-hidden="true"
            >
              <rect
                x="2"
                y="2"
                width="52"
                height="8"
                rx="2"
                fill="currentColor"
                opacity="0.4"
              />
              <rect
                x="2"
                y="14"
                width="52"
                height="10"
                rx="2"
                fill="currentColor"
                opacity="0.9"
              />
            </svg>
            {{ t.form.button.twoLines }}
          </button>
        </div>
      </div>

      <div class="tpl:mb-3">
        <label :class="labelClass">{{ t.form.button.background }}</label>
        <ColorPicker
          :model-value="block.buttonBackgroundColor"
          @update:model-value="updateField('buttonBackgroundColor', $event)"
        />
      </div>
      <div class="tpl:mb-3">
        <label :class="labelClass">{{ t.form.button.textColor }}</label>
        <ColorPicker
          :model-value="block.buttonTextColor"
          @update:model-value="updateField('buttonTextColor', $event)"
        />
      </div>
      <div class="tpl:mb-3">
        <label :class="labelClass">{{ t.form.button.borderRadius }}</label>
        <input
          type="number"
          :class="inputClass"
          min="0"
          max="40"
          :value="block.buttonBorderRadius"
          @input="
            updateField(
              'buttonBorderRadius',
              Number(($event.target as HTMLInputElement).value) || 0,
            )
          "
        />
      </div>
    </div>

    <!-- Terms tab -->
    <div v-else-if="activeTab === 'terms'">
      <label
        class="tpl:mb-3 tpl:flex tpl:cursor-pointer tpl:items-center tpl:gap-2 tpl:text-sm tpl:text-[var(--tpl-text)]"
      >
        <input
          type="checkbox"
          class="tpl:size-3.5 tpl:cursor-pointer tpl:accent-[var(--tpl-primary)]"
          :checked="block.termsEnabled"
          @change="
            updateField(
              'termsEnabled',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        {{ t.form.terms.enable }}
      </label>

      <div v-if="block.termsEnabled">
        <label :class="labelClass">{{ t.form.terms.content }}</label>
        <textarea
          rows="6"
          class="tpl:w-full tpl:resize-y tpl:rounded-[var(--tpl-radius-sm)] tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-2 tpl:text-sm tpl:text-[var(--tpl-text)] tpl:outline-none focus:tpl:border-[var(--tpl-primary)]"
          :value="block.termsContent"
          @input="
            updateField(
              'termsContent',
              ($event.target as HTMLTextAreaElement).value,
            )
          "
        ></textarea>
        <p class="tpl:mt-1 tpl:text-xs tpl:text-[var(--tpl-text-muted)]">
          {{ t.form.terms.htmlHint }}
        </p>
      </div>
    </div>
  </div>
</template>
